import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  AppBar,
  Toolbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { fetchSolicitacoes } from "../api/apiService";
import { useLocation } from "react-router-dom";

const Aviso = () => {
  const [mensagens, setMensagens] = useState([]);
  // Aqui o contador será apenas a soma das solicitações com status ACEITO ou NEGADO
  const [contador, setContador] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [filteredMensagens, setFilteredMensagens] = useState([]);
  const location = useLocation();

  // Extrai o parâmetro "user" da URL
  const params = new URLSearchParams(location.search);
  const user = params.get("user");

  // Função auxiliar para definir a cor do status (como no exemplo anterior)
  const getStatusColor = (status) => {
    switch (status) {
      case "ACEITO":
        return "#2e7d32"; // verde
      case "NEGADO":
        return "#c62828"; // vermelho
      case "PENDENTE":
        return "#ef6c00"; // laranja
      default:
        return "#000000";
    }
  };

  useEffect(() => {
    const obterSolicitacoes = async () => {
      try {
        const solicitacoes = await fetchSolicitacoes(user);
        // Atualiza o contador filtrando apenas os itens com status ACEITO ou NEGADO
        const contadorFiltrado = solicitacoes.filter(
          (s) => s.status === "ACEITO" || s.status === "NEGADO"
        ).length;
        setContador(contadorFiltrado);

        if (solicitacoes.length > 0) {
          const mensagensGeradas = solicitacoes.map((solicitacao) => {
            const {
              categoriaServico,
              nomeServico,
              status,
              dataConclusao,
              protocolo,
              observacao,
            } = solicitacao;
            return {
              protocolo,
              categoriaServico,
              nomeServico,
              status,
              dataConclusao: dataConclusao || "PENDENTE",
              observacao,
            };
          });
          setMensagens(mensagensGeradas);
          setFilteredMensagens(mensagensGeradas);
        } else {
          setMensagens([]);
          setFilteredMensagens([]);
        }
      } catch (error) {
        console.error("Erro ao obter solicitações:", error);
      }
    };

    obterSolicitacoes();
    const interval = setInterval(obterSolicitacoes, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const openPopup = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);

  const filterMensagens = (status) => {
    if (status === "TODOS") {
      setFilteredMensagens(mensagens);
    } else {
      setFilteredMensagens(
        mensagens.filter((mensagem) => mensagem.status === status)
      );
    }
  };

  return (
    <>
      <Box
        onClick={contador > 0 ? openPopup : openPopup}
        sx={{
          marginTop: { xs: "6vh", md: "10.5vh" },
          padding: 2,
          bgcolor: contador > 0 ? "info.light" : "grey.200",
          color: contador > 0 ? "white" : "grey.700",
          borderRadius: 1,
          cursor: "pointer",
          boxShadow: 2,
          textAlign: "center",
          minHeight: "60px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          transition: "background 0.3s ease-in-out, transform 0.2s ease-in-out",
          "&:hover": {
            bgcolor: contador > 0 ? "info.main" : "grey.300",
            transform: "scale(1.05)",
          },
        }}
      >
        <Typography>
          {contador > 0
            ? `📢 ${contador} solicitação(ões) (aceitas/negadas). Clique para ver detalhes (pendentes também).`
            : "Nenhuma solicitação. Clique para ver o histórico."}
        </Typography>
      </Box>

      <Dialog
        fullScreen
        open={isPopupOpen}
        onClose={closePopup}
        sx={{
          "& .MuiDialog-paper": {
            animation: "fadeIn 0.3s ease-in-out",
          },
        }}
      >
        <AppBar sx={{ position: "sticky" }}>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Typography variant="h6">Detalhes das Solicitações</Typography>
            <IconButton color="inherit" onClick={closePopup}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            position: "sticky",
            top: 0,
            backgroundColor: "#f4f6f8",
            zIndex: 10,
            padding: 2,
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
            boxShadow: 1,
            animation: "slideDown 0.3s ease-in-out",
          }}
        >
          <Button variant="contained" onClick={() => filterMensagens("TODOS")}>Todos</Button>
          <Button variant="contained" color="warning" onClick={() => filterMensagens("PENDENTE")}>
            Pendentes
          </Button>
          <Button variant="contained" color="success" onClick={() => filterMensagens("ACEITO")}>
            Aceitas
          </Button>
          <Button variant="contained" color="error" onClick={() => filterMensagens("NEGADO")}>
            Negadas
          </Button>
        </Box>

        <DialogContent sx={{ padding: 2 }}>
          <List>
            {filteredMensagens.map((mensagem, index) => (
              <React.Fragment key={index}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    transition: "background 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                        Protocolo: {mensagem.protocolo}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography>
                          <strong>Categoria:</strong> {mensagem.categoriaServico}
                        </Typography>
                        <Typography>
                          <strong>Serviço:</strong> {mensagem.nomeServico}
                        </Typography>
                        <Typography>
                          <strong>Status:</strong>{" "}
                          <span
                            style={{
                              color: getStatusColor(mensagem.status),
                              fontWeight: "bold",
                            }}
                          >
                            {mensagem.status}
                          </span>
                        </Typography>
                        <Typography>
                          <strong>Data Conclusão:</strong> {mensagem.dataConclusao}
                        </Typography>
                        {mensagem.observacao && (
                          <Typography>
                            <strong>Observação:</strong> {mensagem.observacao}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
          {filteredMensagens.length === 0 && (
            <Typography align="center" sx={{ mt: 2 }}>
              Nenhuma solicitação encontrada para o filtro selecionado.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>

  );
};

export default Aviso;
